import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { httpsCallable } from 'firebase/functions';
import type {
  ConversationMessage,
  GetConversationMessagesResponse,
  MessageAttachmentInput,
} from '../../types/messages';
import { functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import MessageAttachmentPicker from '../messages/MessageAttachmentPicker';
import { messageAttachmentTypeError, readMessageAttachment } from '../../utils/messageAttachment';
import { PRAIRI_SURFACES } from '../../constants/prairiSurfaces';
import greenCheckIcon from '../../assets/investor-pricing/green-check.png';

interface ContactStartupMessagePopupProps {
  isOpen: boolean;
  startupName: string;
  /** Pitch deck / profile thumbnail */
  thumbnailUrl?: string | null;
  /** e.g. "Founder: David Smith" — shown below startup name */
  founderLine?: string | null;
  /** Optional: show "View phone number" link (e.g. wire to contact unlock later) */
  onViewPhoneNumber?: () => void;
  isSending?: boolean;
  /** Shown inside the box when the send is refused, so it is not hidden behind this popup */
  sendError?: string | null;
  /** Investor opted out of the startup knowing they were favorited — sending a message reveals them anyway */
  showDisclosureWarning?: boolean;
  /**
   * The other party's user id. When set, the popup loads the existing thread so the
   * sender can see previous messages before writing again (avoids double-introducing).
   */
  recipientId?: string | null;
  onClose: () => void;
  /** Return false when the send did not go through, so the draft stays in the box. */
  onSend: (message: string, attachment?: MessageAttachmentInput | null) => Promise<boolean>;
}

const MAX_MESSAGE_LENGTH = 2000;

const ContactStartupMessagePopup: React.FC<ContactStartupMessagePopupProps> = ({
  isOpen,
  startupName,
  thumbnailUrl,
  founderLine,
  onViewPhoneNumber,
  isSending = false,
  sendError = null,
  showDisclosureWarning = false,
  recipientId = null,
  onClose,
  onSend,
}) => {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [place, setPlace] = useState<{ right: number; bottom: number; width: number } | null>(null);
  const [previousMessages, setPreviousMessages] = useState<ConversationMessage[]>([]);
  const [previousState, setPreviousState] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle'
  );
  const threadRef = useRef<HTMLDivElement>(null);
  const loadRequestRef = useRef(0);

  const loadPrevious = useCallback(
    async (mode: 'initial' | 'refresh') => {
      if (!recipientId || !user?.id) {
        setPreviousMessages([]);
        setPreviousState('idle');
        return;
      }
      const requestId = ++loadRequestRef.current;
      const investorId = user.userType === 'investor' ? user.id : recipientId;
      const startupId = user.userType === 'investor' ? recipientId : user.id;
      const conversationId = `${investorId}_${startupId}`;
      if (mode === 'initial') setPreviousState('loading');
      try {
        const getMessages = httpsCallable<
          { conversationId: string; limit?: number },
          GetConversationMessagesResponse
        >(functions, 'getConversationMessages');
        const result = await getMessages({ conversationId, limit: 50 });
        if (loadRequestRef.current !== requestId) return;
        const data = result.data;
        if (!data.success) {
          if (mode === 'refresh') return;
          setPreviousMessages([]);
          // No thread yet is a real answer. Any other failure must stay visible so a
          // sender does not assume this is a first introduction.
          setPreviousState(data.error === 'Conversation not found' ? 'ready' : 'error');
          return;
        }
        setPreviousMessages(data.messages);
        setPreviousState('ready');
      } catch {
        if (loadRequestRef.current !== requestId || mode === 'refresh') return;
        setPreviousMessages([]);
        setPreviousState('error');
      }
    },
    [recipientId, user?.id, user?.userType]
  );

  useEffect(() => {
    if (!isOpen) {
      setMessage('');
      setAttachmentFile(null);
      setAttachmentError(null);
    }
  }, [isOpen]);

  // Load the existing thread so the sender sees what's already been said.
  useEffect(() => {
    if (!isOpen || !recipientId || !user?.id) {
      loadRequestRef.current += 1;
      setPreviousMessages([]);
      setPreviousState('idle');
      return;
    }
    void loadPrevious('initial');
  }, [isOpen, recipientId, user?.id, loadPrevious]);

  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [previousMessages]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSending) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSending, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setPlace(null);
      return;
    }
    const update = () => {
      const margin = 12;
      if (window.innerWidth < 640) {
        setPlace(null);
        return;
      }
      setPlace({
        right: margin,
        bottom: margin,
        width: Math.min(520, window.innerWidth - margin * 2),
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmedMessage = message.trim();
  const isDisabled = isSending || (trimmedMessage.length === 0 && !attachmentFile);

  const handleSubmit = async () => {
    if (isDisabled) return;
    let attachment: MessageAttachmentInput | null = null;
    if (attachmentFile) {
      try {
        attachment = await readMessageAttachment(attachmentFile);
      } catch (error) {
        setAttachmentError(error instanceof Error ? error.message : 'Could not read that file.');
        return;
      }
    }
    const sent = await onSend(trimmedMessage, attachment);
    if (sent === false) return;
    const sentBody = trimmedMessage;
    const sentFile = attachment;
    setMessage('');
    setAttachmentFile(null);
    setAttachmentError(null);
    setPreviousMessages(current => [
      ...current,
      {
        id: `pending-${Date.now()}`,
        senderId: user?.id || '',
        recipientId: recipientId || '',
        senderType: user?.userType === 'investor' ? 'investor' : 'startup',
        body: sentBody,
        createdAt: new Date().toISOString(),
        readAt: null,
        delivery: null,
        attachment: sentFile
          ? { name: sentFile.name, contentType: sentFile.contentType, url: '', size: 0 }
          : null,
      },
    ]);
    setPreviousState('ready');
    await loadPrevious('refresh');
  };

  return createPortal(
    <div
      className={
        place
          ? 'fixed z-[100000] pointer-events-none'
          : 'fixed bottom-0 inset-x-0 z-[100000] pointer-events-none flex justify-end px-3 sm:inset-x-auto sm:right-6 sm:w-[min(32.5rem,calc(100%-2rem))]'
      }
      style={place ? { right: place.right, bottom: place.bottom, width: place.width } : undefined}
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-message-dialog-title"
    >
      <div
        className="w-full max-h-[calc(100dvh-0.75rem)] overflow-y-auto rounded-t-2xl border border-gray-800 shadow-2xl pointer-events-auto"
        style={{ backgroundColor: '#141414', fontFamily: 'Rubik, sans-serif' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header row */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <h2 id="new-message-dialog-title" className="text-white text-base font-medium">
            New message
          </h2>
          <div className="flex items-center">
            <button
              type="button"
              onClick={onClose}
              disabled={isSending}
              className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Startup row: larger square thumbnail + name / founders + tip under names */}
        <div className="px-4 pb-3 flex gap-4 items-start">
          <div className="flex-shrink-0 h-28 w-28 overflow-hidden bg-[#222] border border-gray-700 rounded-sm sm:h-32 sm:w-32">
            {thumbnailUrl ? (
              <img src={thumbnailUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">
                No image
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 pt-0.5 space-y-2">
            <div>
              <p className="text-white font-semibold text-lg leading-tight break-words">
                {startupName}
              </p>
              {founderLine ? (
                <p className="text-white/90 text-base mt-1.5 break-words">{founderLine}</p>
              ) : null}
              {onViewPhoneNumber ? (
                <button
                  type="button"
                  onClick={onViewPhoneNumber}
                  className="mt-2 text-sm text-emerald-400 hover:text-emerald-300 underline underline-offset-2 text-left"
                >
                  View phone number
                </button>
              ) : null}
            </div>
            {showDisclosureWarning ? (
              <div className="space-y-1.5 rounded-lg border border-[#444444] bg-[#1E1E1E] px-3 py-2.5">
                <p
                  className="flex items-start gap-2 text-sm leading-snug"
                  style={{ color: '#FFC000' }}
                >
                  <span
                    className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: '#FFC000' }}
                    aria-hidden
                  />
                  <span>Profiles are shared when investors message startups</span>
                </p>
                <p
                  className="flex items-start gap-2 text-sm leading-snug"
                  style={{ color: PRAIRI_SURFACES.trackerComplete }}
                >
                  <img
                    src={greenCheckIcon}
                    alt=""
                    aria-hidden
                    className="mt-0.5 h-4 w-4 flex-shrink-0 object-contain"
                  />
                  <span>Your phone and email remain private</span>
                </p>
              </div>
            ) : null}
          </div>
        </div>

        {/* Green divider */}
        <div className="h-px bg-emerald-500 mx-4" aria-hidden />

        {/* Existing thread — so the sender sees prior messages before writing again. */}
        {previousState === 'loading' ? (
          <div className="px-4 pt-3 text-xs text-gray-400">Checking previous messages…</div>
        ) : previousState === 'error' ? (
          <div className="px-4 pt-3 text-xs text-gray-400">
            Couldn&apos;t load previous messages. Open Messages before sending another introduction.
          </div>
        ) : previousState === 'ready' && previousMessages.length === 0 ? (
          <div className="px-4 pt-3 text-xs text-gray-400">No previous messages</div>
        ) : previousMessages.length > 0 ? (
          <div className="px-4 pt-3">
            <p className="mb-2 text-xs font-medium text-gray-400">Previous messages</p>
            <div ref={threadRef} className="max-h-56 space-y-2 overflow-y-auto pr-1">
              {previousMessages.map(m => {
                const mine = m.senderId === user?.id;
                return (
                  <div key={m.id} className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                    <span className="mb-0.5 text-[10px] text-gray-500">
                      {mine ? 'You' : startupName}
                      {m.createdAt ? ` · ${new Date(m.createdAt).toLocaleDateString()}` : ''}
                    </span>
                    <div
                      className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-2.5 py-1.5 text-sm leading-snug break-words ${
                        mine ? 'bg-emerald-700/30 text-white' : 'bg-[#1E1E1E] text-gray-200'
                      }`}
                    >
                      {m.body ? m.body : m.attachment ? `📎 ${m.attachment.name}` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* Message area */}
        <div className="p-4 pt-4">
          <div className="rounded-xl bg-[#1E1E1E] border border-gray-700/80 overflow-hidden min-h-[160px] flex flex-col">
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
              placeholder="Write a message..."
              className="flex-1 w-full min-h-[140px] bg-transparent text-white placeholder:text-gray-500 px-3 py-3 text-base resize-none focus:outline-none focus:ring-0"
              maxLength={MAX_MESSAGE_LENGTH}
              disabled={isSending}
            />
            <div className="border-t border-gray-700/80">
              {sendError ? (
                <p className="px-3 pt-2 text-xs text-red-500" role="alert">
                  {sendError}
                </p>
              ) : null}
              <div className="flex items-center justify-between gap-2 px-3 pt-2 pb-2">
                <div className="flex min-w-0 items-center gap-2">
                  <MessageAttachmentPicker
                    fileName={attachmentFile?.name ?? null}
                    disabled={isSending}
                    error={attachmentError}
                    onFile={file => {
                      const error = messageAttachmentTypeError(file);
                      if (error) {
                        setAttachmentError(error);
                        setAttachmentFile(null);
                        return;
                      }
                      setAttachmentError(null);
                      setAttachmentFile(file);
                    }}
                    onClear={() => {
                      setAttachmentFile(null);
                      setAttachmentError(null);
                    }}
                  />
                  <span className="flex-shrink-0 text-xs text-gray-500">
                    {message.length}/{MAX_MESSAGE_LENGTH}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isSending}
                    className="px-3 py-1.5 text-xs font-medium rounded-full text-white hover:bg-white/10 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isDisabled}
                    className="px-4 py-1.5 text-xs font-semibold rounded-full bg-emerald-700 text-white hover:bg-emerald-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                  >
                    {isSending && (
                      <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    )}
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ContactStartupMessagePopup;
